import type { CSSProperties } from 'react';
import type { BaseImageProps } from './base-image.types';
import { useImageLoad } from './hooks';
import { getLayoutStyles, getResponsiveAttrs } from './utils';

/**
 * Drop-in replacement for next/image and next/legacy/image with full API compatibility.
 * Supports: src, alt, width, height, fill, quality, priority, placeholder, loading, className, and style.
 */
export function BaseImage({
  src,
  alt,
  width,
  height,
  fill,
  loader,
  className,
  style,
  objectFit,
  objectPosition,
  layout,
  priority = false,
  quality = 75,
  placeholder,
  blurDataURL,
  loading,
  sizes,
  'data-testid': dataTestId,
  'aria-hidden': ariaHidden,
  onError,
  onLoad,
  isLegacyMode,
  ...restProps
}: BaseImageProps) {
  // Use image load hook for onLoad
  const imgRef = useImageLoad(onLoad);

  // Determine if we're in fill mode (either explicit fill or layout="fill")
  const isFillMode = fill || layout === 'fill';

  // Generate the src URL using the loader if provided
  const srcUrl = loader
    ? loader({ src, width: typeof width === 'number' ? width : undefined, quality })
    : src;

  // Determine layout styles
  const layoutStyles = getLayoutStyles({
    isFillMode,
    layout,
    objectFit,
    objectPosition,
    width,
    height,
    isLegacyMode,
  });

  // Combine layout styles with user-provided styles
  const combinedStyle: CSSProperties = {
    ...layoutStyles,
    ...style,
  };

  // Add blur placeholder styles if provided
  //Cannot access refs during render
  // eslint-disable-next-line
  if (placeholder === 'blur' && blurDataURL && !imgRef.current?.complete) {
    combinedStyle.backgroundImage = `url(${blurDataURL})`;
    combinedStyle.backgroundSize = 'cover';
    combinedStyle.backgroundPosition = 'center';
  }

  // Determine loading strategy
  const loadingStrategy = priority ? 'eager' : loading || 'lazy';

  // Build img attributes
  const imgAttributes: Record<string, unknown> = {
    src: srcUrl,
    alt,
    className,
    style: combinedStyle,
    loading: loadingStrategy,
    onError,
    'aria-hidden': ariaHidden,
    decoding: 'async',
    ...restProps,
  };

  // Add data-testid separately to avoid type issues
  if (dataTestId) {
    imgAttributes['data-testid'] = dataTestId;
  }

  if (isFillMode) {
    imgAttributes['data-nimg'] = 'fill';
  }

  // Add width/height if not using fill mode
  if (!isFillMode) {
    if (width !== undefined) imgAttributes.width = width;
    if (height !== undefined) imgAttributes.height = height;
  }

  // For layout=responsive, generate srcset and sizes matching next/legacy/image behavior
  if (layout === 'responsive' && !isFillMode && isLegacyMode) {
    const { srcSet, sizes: computedSizes } = getResponsiveAttrs({ src, quality, loader, sizes });
    imgAttributes.srcSet = srcSet;
    imgAttributes.sizes = computedSizes;
  } else if (sizes !== undefined && !isLegacyMode) {
    imgAttributes.sizes = sizes;
  }

  return <img ref={imgRef} {...imgAttributes} />;
}

export default BaseImage;
